<template>
  <main class="main main--has-floating-buttons">
    <template v-if="dataLoaded === true">
      <portal to="headerTitle">
        {{ taxSet.name }}
      </portal>

      <card :title="$t('admin.pageTaxSet.card.name')" cols="12 6@desktop start-1">
        <hm-input v-model="taxSet.name"
                  :lazy="true"
                  :disabled="taxSet.deleted"
                  :label="$t('admin.pageTaxSet.title')"
                  validate="required"
                  name="title"
                  cols="12" />
      </card>

      <card :title="$t('admin.pageTaxSet.card.taxes')" cols="12 start-1">
        <hm-card-inner v-for="(it, idx) in taxSet.taxReferences" :title="`${$t('admin.pageTaxSet.card.tax')} #${idx+1}`" :key="idx">
          <pre v-html="idx" />
        </hm-card-inner>
        <hm-card-inner :title="`${$t('admin.pageTaxSet.card.tax')} #${taxSet.taxReferences.length + 1}`">

        </hm-card-inner>
      </card>
      <pre class="col-12" v-html="taxSet" />

      <card v-if="!canDelete" :title="$t('admin.pageTaxSet.card.usage')" cols="12" card-type="collapsible">
        <hm-card-inner v-if="!isEmpty(usedRatePlans)" :full-size="true" :title="$t('admin.pageTaxSet.card.usageRatePlans')">
          <hm-col cols="12">
            <template v-for="it in usedRatePlans" v-if="!it.deleted">
              <hm-card-inner-line :title="it.name[$shortLocale]" :clickable="true" :key="it.id" @click="routeToRatePlan(it)" />
            </template>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner v-if="!isEmpty(usedInHouseServices)" :full-size="true" :title="$t('admin.pageTaxSet.card.usageInHouseServices')">
          <hm-col cols="12">
            <template v-for="it in usedInHouseServices" v-if="!it.deleted">
              <hm-card-inner-line :title="it.name[$shortLocale]" :clickable="true" :key="it.id" @click="routeToInHouseService(it)" />
            </template>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner v-if="!isEmpty(usedMealPlans)" :full-size="true" :title="$t('admin.pageTaxSet.card.usageInMealPlans')">
          <hm-col cols="12">
            <template v-for="it in usedMealPlans" v-if="!it.deleted">
              <hm-card-inner-line :title="it.name[$shortLocale]" :clickable="true" :key="it.id" @click="routeToMealPlan(it)" />
            </template>
          </hm-col>
        </hm-card-inner>

        <hm-floating-buttons id="actions" ref="floatingButtons">
          <hm-floating-button v-if="!taxSet.deleted && canDelete && hasRole('ROLE_SYSADMIN')"
                              :label="$t('admin.pageTaxSet.actions.deleteTaxSet')"
                              icon="delete"
                              color="error"
                              @click="deleteTaxSet(false)" />
        </hm-floating-buttons>
      </card>
    </template>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import uuid from 'uuid';
import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { cloneDeep, groupBy, isEmpty, isNil, keyBy, sortBy, size } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import HmFloatingButton from '../../lib/inputs/HmFloatingButton';
import HmCardInner from '../../lib/layout/HmCardInner';
import HmMultiselect from '../../lib/inputs/HmMultiselect';
import HmInputDate from '../../lib/inputs/HmInputDate';

export default {
  name: 'PageTaxSet',
  components: { HmInputDate, HmMultiselect, HmCardInner, HmFloatingButton },
  autosave: [
    {
      name: 'taxSet',
      variable: 'taxSet',
      event: 'hotelSettings.update',
      fn: function (cbSaveComplete) {
        if (this.taxSet.deleted === true) {
          cbSaveComplete();
          return;
        }

        const toSave = cloneDeep(this.taxSet);

        this.axios.put(this.serviceUrls.taxSet.get(this, this.taxSetId), toSave)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();

            this.$serviceBus.$emit('taxSet.list');
          })
          .catch((error) => {
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  mixins: [HotelmeisterMixin, SaveWatchMixin],

  data() {
    return {
      taxSetId: this.$route.params.taxSetId,
      taxSet: { },
      newTaxReference: {
        taxId: null,
        seasonValidFrom: null,
        seasonValidUntil: null,
        validityDateRanges: [
          {
            startDate: null,
            endDate: null,
          },
        ],
      },
      dataLoaded: false,
    };
  },

  computed: {
    usedRatePlans: function () {
      return this.hotelSettings.ratePlans
        .filter(it => !it.deleted && it.taxSet === this.taxSet.id);
    },
    usedInHouseServices: function () {
      return this.hotelSettings.inHouseServices
        .filter(it => !it.deleted && it.taxSet === this.taxSet.id);
    },
    usedMealPlans: function () {
      return this.hotelSettings.mealPlans
        .filter(it => !it.deleted && it.taxSet === this.taxSet.id);
    },
    usedAnywhere: function () {
      return this.usedRatePlans.length > 0 || this.usedInHouseServices.length > 0 || this.usedMealPlans.length > 0;
    },
    canDelete: function () {
      return this.usedRatePlans.length === 0 && this.usedInHouseServices.length === 0 && this.usedMealPlans.length === 0;
    },
  },

  watch: {
    'taxSet.taxSetType': function (newVal) {
      if (newVal === 'VAT') {
        this.taxSet.percentage = true;
        this.taxSet.inclusiveTaxSet = isNil(this.hotelSettings.details.vatIncluded) ? true : this.hotelSettings.details.vatIncluded;
      } else {
        this.taxSet.vatLabel = null;
      }
    },
    taxSet: function (newVal) {
      if (isNil(newVal)) {
        this.dataLoaded = false;
      }
    },
  },

  created: function () {
    this.loadData();
    this.$serviceBus.$emit('taxSet.list');
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    addValidityDateRange() {
      if (this.taxSet.validityDateRanges == null) {
        this.taxSet.validityDateRanges = [];
      }
      this.taxSet.validityDateRanges.push({
        id: uuid.v4(),
        startDate: null,
        endDate: null,
      });
    },
    deleteValidityDateRange(dateRange) {
      const idx = this.taxSet.validityDateRanges.indexOf(dateRange);
      if (idx >= 0) {
        this.taxSet.validityDateRanges.splice(idx, 1);
      }
    },
    routeToRatePlan(ratePlan) {
      this.$router.push({
        name: 'ratePlan',
        params: {
          ratePlanId: ratePlan.id,
        },
      });
    },
    routeToRoomType(roomType) {
      this.$router.push({
        name: 'roomType',
        params: {
          roomTypeId: roomType.id,
        },
      });
    },
    routeToMealPlan(mealPlan) {
      this.$router.push({
        name: 'mealPlan',
        params: {
          mealPlanId: mealPlan.id,
        },
      });
    },
    routeToInHouseService(inHouseService) {
      this.$router.push({
        name: 'inHouseService',
        params: {
          inHouseServiceId: inHouseService.id,
        },
      });
    },
    loadData() {
      this.axios.get(this.serviceUrls.taxSet.get(this, this.taxSetId))
        .then((response) => {
          if (this.dataLoaded === false) {
            const taxSet = response.data.data;
            if (!isNil(taxSet.seasonalValidFrom)) {
              taxSet.seasonalValidFrom = this.$moment(`${taxSet.seasonalValidFrom[0]}-${taxSet.seasonalValidFrom[1]}`, 'DD-MM').format(this.dayMonthFormat);
            }
            if (!isNil(taxSet.seasonalValidUntil)) {
              taxSet.seasonalValidUntil = this.$moment(`${taxSet.seasonalValidUntil[0]}-${taxSet.seasonalValidUntil[1]}`, 'DD-MM').format(this.dayMonthFormat);
            }
            this.taxSet = taxSet;
            this.dataLoaded = true;
          }
        });
    },
    deleteTaxSet(confirmed = false) {
      if (this.canDelete) {
        if (confirmed === true) {
          this.axios.delete(this.serviceUrls.taxSet.get(this, this.taxSetId))
            .then((response) => {
              this.dataLoaded = false;
              this.taxSet = response.data.data;
              this.showSuccessMessage();
              this.$serviceBus.$emit('hotelSettings.update');
              this.$serviceBus.$emit('taxSet.list');
              this.$router.push({
                name: 'taxSets',
              });
            })
            .catch((error) => {
              this.showMessages(error.response);
            })
            .catch((error) => {
              if (isNil(error.response)) this.showErrorMessage();
              this.showMessages(error.response);
            });
        } else {
          this.showConfirmModal(
            this.$t('admin.pageTaxSet.confirmDeleteTaxSet'),
            (doNotShowAgain) => {
              this.deleteTaxSet(true);
            },
          );
        }
      } else {
        this.showErrorMessage(this.$t('admin.pageTaxSet.taxSetInUse'));
      }
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PageTaxSet.vue